import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Upload, ChevronLeft, ChevronRight, X, Image, Loader2, Calendar, RefreshCw, User, CalendarDays, FileSpreadsheet, Check, Pencil, Trash2, AlertCircle } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isWeekend, getDaysInMonth as getDaysCount, isPast, isToday, startOfDay } from 'date-fns';

// UK Bank Holidays 2026
const UK_BANK_HOLIDAYS_2026 = [
  '2026-01-01', // New Year's Day
  '2026-04-03', // Good Friday
  '2026-04-06', // Easter Monday
  '2026-05-04', // Early May Bank Holiday
  '2026-05-25', // Spring Bank Holiday
  '2026-08-31', // Summer Bank Holiday
  '2026-12-25', // Christmas Day
  '2026-12-28', // Boxing Day (substitute)
];

const GroupAbsenceCalendar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date()); // Start at current month
  const [leaveData, setLeaveData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [uploadMode, setUploadMode] = useState('screenshot'); // 'screenshot' or 'csv'
  const [previewRecords, setPreviewRecords] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [savingVerified, setSavingVerified] = useState(false);
  const fileInputRef = useRef(null);
  const csvInputRef = useRef(null);

  // Encon brand colors
  const enconOrange = '#ee7203';
  const enconBlack = '#1d1d1b';
  const enconGrey = '#9d9d9c';

  // Month options for mobile dropdown
  const monthOptions = [
    { value: '2026-01', label: 'January 2026' },
    { value: '2026-02', label: 'February 2026' },
    { value: '2026-03', label: 'March 2026' },
    { value: '2026-04', label: 'April 2026' },
    { value: '2026-05', label: 'May 2026' },
    { value: '2026-06', label: 'June 2026' },
    { value: '2026-07', label: 'July 2026' },
    { value: '2026-08', label: 'August 2026' },
    { value: '2026-09', label: 'September 2026' },
    { value: '2026-10', label: 'October 2026' },
    { value: '2026-11', label: 'November 2026' },
    { value: '2026-12', label: 'December 2026' },
  ];

  // Load leave data
  const loadLeaveData = useCallback(async () => {
    setLoading(true);
    try {
      const monthStr = format(currentMonth, 'yyyy-MM');
      const response = await api.get(`${API}/group-absence/month/${monthStr}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setLeaveData(response.data.leave_records || []);
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error loading leave data:', error);
      if (error.response?.status === 404) {
        setLeaveData([]);
        setEmployees([]);
      } else {
        toast.error('Failed to load calendar data');
      }
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    loadLeaveData();
  }, [loadLeaveData]);

  // Navigate months
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  // Handle month change from dropdown
  const handleMonthChange = (value) => {
    const [year, month] = value.split('-');
    setCurrentMonth(new Date(parseInt(year), parseInt(month) - 1, 1));
  };

  // Get days for calendar grid (all days including weekends)
  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  };

  const days = getDaysInMonth();
  const daysCount = days.length;

  // Check if day is a bank holiday
  const isBankHoliday = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return UK_BANK_HOLIDAYS_2026.includes(dateStr);
  };

  // Get employee leave for a specific day (only holidays, not sick)
  const getEmployeeLeaveForDay = (employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return leaveData.find(l => 
      l.employee_id === employeeId && 
      l.leave_type === 'holiday' &&
      dateStr >= l.start_date && 
      dateStr <= l.end_date
    );
  };

  // Format name to first name first
  const formatName = (name) => {
    if (!name) return '';
    const parts = name.split(', ');
    if (parts.length === 2) {
      return `${parts[1]} ${parts[0]}`;
    }
    return name;
  };

  // Handle file drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
    }
  }, []);

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle file input change
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).filter(file => 
      file.type.startsWith('image/')
    );
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  // Handle paste
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData.items;
    const files = [];
    
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) files.push(file);
      }
    }
    
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
      toast.success(`${files.length} image(s) pasted`);
    }
  }, []);

  // Global paste listener when modal is open
  useEffect(() => {
    if (showUploadModal) {
      window.addEventListener('paste', handlePaste);
      return () => window.removeEventListener('paste', handlePaste);
    }
  }, [showUploadModal, handlePaste]);

  // Remove file from selection
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload and get preview for verification
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });
      
      const syncMonth = format(currentMonth, 'yyyy-MM');
      formData.append('sync_month', syncMonth);

      // Use preview endpoint for verification
      const response = await api.post(`${API}/group-absence/parse-screenshots-preview`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        timeout: 120000
      });

      const records = response.data.preview_records || [];
      if (records.length === 0) {
        toast.warning('No leave records found in the uploaded images');
        return;
      }

      // Show preview for verification
      setPreviewRecords(records);
      setShowUploadModal(false);
      setShowPreviewModal(true);
      toast.info(`Extracted ${records.length} records - please verify before saving`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process screenshots');
    } finally {
      setUploading(false);
    }
  };

  // Handle CSV/Excel file upload
  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const syncMonth = format(currentMonth, 'yyyy-MM');
      formData.append('sync_month', syncMonth);

      const response = await api.post(`${API}/group-absence/import-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        timeout: 60000
      });

      const records = response.data.preview_records || [];
      if (records.length === 0) {
        toast.warning('No valid records found in the file');
        return;
      }

      setPreviewRecords(records);
      setShowUploadModal(false);
      setShowPreviewModal(true);
      toast.info(`Imported ${records.length} records - please verify before saving`);
    } catch (error) {
      console.error('CSV import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import file');
    } finally {
      setUploading(false);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  };

  // Save verified records
  const handleSaveVerified = async () => {
    if (previewRecords.length === 0) {
      toast.error('No records to save');
      return;
    }

    setSavingVerified(true);
    try {
      const syncMonth = format(currentMonth, 'yyyy-MM');
      const response = await api.post(`${API}/group-absence/save-verified`, previewRecords, {
        params: { sync_month: syncMonth },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      const { records_added = 0, records_updated = 0 } = response.data;
      toast.success(`Saved successfully: ${records_added} added, ${records_updated} updated`);
      
      setShowPreviewModal(false);
      setPreviewRecords([]);
      setSelectedFiles([]);
      loadLeaveData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save records');
    } finally {
      setSavingVerified(false);
    }
  };

  // Edit a preview record
  const handleEditRecord = (index, field, value) => {
    const updated = [...previewRecords];
    updated[index] = { ...updated[index], [field]: value };
    setPreviewRecords(updated);
  };

  // Delete a preview record
  const handleDeleteRecord = (index) => {
    setPreviewRecords(prev => prev.filter((_, i) => i !== index));
  };

  // Add a new record manually
  const handleAddRecord = () => {
    const monthStr = format(currentMonth, 'yyyy-MM');
    setPreviewRecords(prev => [...prev, {
      employee_id: '',
      employee_name: '',
      start_date: `${monthStr}-01`,
      end_date: `${monthStr}-01`,
      leave_type: 'holiday',
      employee_exists: false
    }]);
  };

  // Get days and calculate cell width for fitting to screen
  const filteredEmployees = selectedEmployee === 'all' 
    ? employees 
    : employees.filter(e => e.id === selectedEmployee);

  // Mobile menu buttons
  const mobileMenuButtons = [
    {
      icon: Upload,
      label: 'Upload Screenshots',
      onClick: () => setShowUploadModal(true),
      color: enconOrange
    },
    {
      icon: RefreshCw,
      label: 'Refresh Data',
      onClick: loadLeaveData,
      color: enconOrange
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Global Header */}
      <AppHeader 
        user={user} 
        onLogout={onLogout}
        subHeaderLeft={
          <>
            <div className="hidden md:flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
              <Calendar className="w-3.5 h-3.5 text-white/90 flex-shrink-0" />
              <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Annual Leave Calendar</span>
            </div>
            <div className="hidden md:flex items-center gap-1.5">
              <button
                onClick={prevMonth}
                className="p-1.5 rounded-full bg-white/90 hover:bg-white transition-all"
                style={{ color: enconBlack }}
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <Select value={format(currentMonth, 'yyyy-MM')} onValueChange={handleMonthChange}>
                <SelectTrigger 
                  className="px-3 py-1 rounded-full text-white font-bold text-xs sm:text-sm min-w-[130px] text-center border-0 cursor-pointer h-auto"
                  style={{ backgroundColor: enconOrange }}
                >
                  <SelectValue>{format(currentMonth, 'MMMM yyyy')}</SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {monthOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                onClick={nextMonth}
                className="p-1.5 rounded-full bg-white/90 hover:bg-white transition-all"
                style={{ color: enconBlack }}
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        }
        subHeaderRight={
          <button
            onClick={() => setShowUploadModal(true)}
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors"
            style={{ color: enconOrange }}
          >
            <Upload className="w-3.5 h-3.5" />
            Upload
          </button>
        }
        mobileMenuButtons={mobileMenuButtons}
      />

      {/* Main Content */}
      <div className="pt-[20px] px-2 md:px-4 pb-4 bg-gray-100 min-h-screen">
        <div className="max-w-full mx-auto">

          {/* Mobile Controls */}
          <div className="md:hidden space-y-3 mb-4">
            <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-3 border border-gray-200">
              <Calendar className="w-5 h-5" style={{ color: enconOrange }} />
              <h1 className="text-lg font-bold" style={{ color: enconBlack }}>Annual Leave</h1>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {/* Month Selector */}
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4" style={{ color: enconGrey }} />
                <Select value={format(currentMonth, 'yyyy-MM')} onValueChange={handleMonthChange}>
                  <SelectTrigger className="flex-1 bg-white border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {monthOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Person Selector */}
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" style={{ color: enconGrey }} />
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger className="flex-1 bg-white border-gray-300">
                    <SelectValue placeholder="All Staff" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{formatName(emp.name)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mb-3 px-1">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#8b5cf6' }}></div>
              <span className="text-sm" style={{ color: enconBlack }}>Annual Leave</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-400"></div>
              <span className="text-sm" style={{ color: enconBlack }}>Bank Holiday</span>
            </div>
          </div>

          {/* Calendar Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-64 bg-gray-100 rounded-xl border border-gray-200">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: enconOrange }} />
              <span className="ml-2" style={{ color: enconGrey }}>Loading calendar...</span>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-300 overflow-hidden">
              {/* Desktop Calendar */}
              <div className="hidden md:block">
                <table className="w-full border-collapse table-fixed">
                  <thead>
                    <tr className="bg-gray-100">
                      <th 
                        className="sticky left-0 z-10 bg-gray-100 px-2 py-2 text-left text-sm font-bold border-b border-r border-gray-300"
                        style={{ color: enconBlack, width: '160px' }}
                      >
                        Employee
                      </th>
                      {days.map((day) => {
                        const dayNum = format(day, 'd');
                        const dayName = format(day, 'EEE');
                        const weekend = isWeekend(day);
                        const bankHol = isBankHoliday(day);
                        const elapsed = isPast(startOfDay(day)) && !isToday(day);
                        return (
                          <th 
                            key={day.toISOString()} 
                            className={`px-0 py-1 text-center text-xs font-medium border-b border-gray-300 ${
                              weekend || bankHol ? 'bg-gray-200' : ''
                            }`}
                            style={{ 
                              color: weekend || bankHol ? enconGrey : enconBlack,
                              width: `${(100 - 12) / daysCount}%`,
                              backgroundColor: weekend || bankHol 
                                ? '#e5e7eb' 
                                : elapsed ? 'rgba(238, 114, 3, 0.12)' : undefined
                            }}
                          >
                            <div className="flex flex-col items-center leading-tight">
                              <span className="text-[9px] uppercase">{dayName.substring(0, 2)}</span>
                              <span className="font-bold text-xs">{dayNum}</span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  
                  <tbody>
                    {employees.length === 0 ? (
                      <tr>
                        <td colSpan={days.length + 1} className="py-8 text-center" style={{ color: enconGrey }}>
                          <div className="flex flex-col items-center gap-2">
                            <Calendar className="w-10 h-10 text-gray-400" />
                            <p>No employee data yet</p>
                            <Button
                              onClick={() => setShowUploadModal(true)}
                              className="mt-2 text-white"
                              style={{ backgroundColor: enconOrange }}
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              Upload Screenshots
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      employees.map((employee, empIndex) => (
                        <tr 
                          key={employee.id} 
                          className={`${empIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-orange-50 transition-colors border-b border-gray-300`}
                        >
                          <td 
                            className="sticky left-0 z-10 px-2 py-1.5 border-r border-gray-300 border-b border-gray-300 font-semibold text-sm"
                            style={{ 
                              color: enconBlack,
                              backgroundColor: empIndex % 2 === 0 ? 'white' : '#f9fafb'
                            }}
                          >
                            {formatName(employee.name)}
                          </td>
                          
                          {days.map((day) => {
                            const leave = getEmployeeLeaveForDay(employee.id, day);
                            const weekend = isWeekend(day);
                            const bankHol = isBankHoliday(day);
                            const hasLeave = !!leave && !weekend; // Don't show leave on weekends
                            const elapsed = isPast(startOfDay(day)) && !isToday(day);
                            
                            return (
                              <td 
                                key={day.toISOString()} 
                                className={`px-0 py-1 text-center ${
                                  weekend || bankHol ? 'bg-gray-200' : ''
                                }`}
                                style={{ 
                                  borderLeft: '1px solid #d1d5db', 
                                  borderBottom: '1px solid #d1d5db',
                                  backgroundColor: weekend || bankHol 
                                    ? '#e5e7eb' 
                                    : elapsed ? 'rgba(238, 114, 3, 0.12)' : undefined
                                }}
                              >
                                {bankHol && !hasLeave && !weekend && (
                                  <div 
                                    className="mx-auto w-5 h-5 rounded bg-gray-400"
                                    title="Bank Holiday"
                                  />
                                )}
                                {hasLeave && (
                                  <div 
                                    className="mx-auto w-5 h-5 rounded"
                                    style={{ backgroundColor: '#8b5cf6' }}
                                    title={`Annual Leave - ${formatName(employee.name)}`}
                                  />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Calendar - Vertical List */}
              <div className="md:hidden">
                {filteredEmployees.length === 0 ? (
                  <div className="py-8 text-center" style={{ color: enconGrey }}>
                    <Calendar className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                    <p>No employee data</p>
                  </div>
                ) : (
                  filteredEmployees.map((employee) => {
                    const employeeLeave = leaveData.filter(l => 
                      l.employee_id === employee.id && 
                      l.leave_type === 'holiday'
                    );
                    
                    return (
                      <div key={employee.id} className="border-b border-gray-200 p-3">
                        <div className="font-bold text-base mb-2" style={{ color: enconBlack }}>
                          {formatName(employee.name)}
                        </div>
                        
                        {/* Mini Calendar Grid for Mobile */}
                        <div className="grid grid-cols-7 gap-0.5 text-center">
                          {/* Day headers */}
                          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                            <div key={i} className="text-[10px] font-medium" style={{ color: enconGrey }}>
                              {d}
                            </div>
                          ))}
                          
                          {/* Empty cells for first week offset */}
                          {Array.from({ length: (getDay(startOfMonth(currentMonth)) + 6) % 7 }).map((_, i) => (
                            <div key={`empty-${i}`} />
                          ))}
                          
                          {/* Day cells */}
                          {days.map((day) => {
                            const dateStr = format(day, 'yyyy-MM-dd');
                            const hasLeave = employeeLeave.some(l => dateStr >= l.start_date && dateStr <= l.end_date);
                            const weekend = isWeekend(day);
                            const bankHol = isBankHoliday(day);
                            
                            return (
                              <div 
                                key={day.toISOString()}
                                className={`w-7 h-7 flex items-center justify-center text-[10px] rounded ${
                                  hasLeave ? 'text-white font-bold' : 
                                  bankHol ? 'bg-gray-300 text-gray-600' :
                                  weekend ? 'text-gray-400' : ''
                                }`}
                                style={{ 
                                  backgroundColor: hasLeave ? '#8b5cf6' : undefined,
                                  color: hasLeave ? 'white' : undefined
                                }}
                              >
                                {format(day, 'd')}
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Leave summary */}
                        {employeeLeave.length > 0 && (
                          <div className="mt-2 text-xs" style={{ color: enconGrey }}>
                            {employeeLeave.map((leave, idx) => (
                              <span key={idx} className="mr-2">
                                {leave.start_date === leave.end_date 
                                  ? format(new Date(leave.start_date), 'd MMM')
                                  : `${format(new Date(leave.start_date), 'd')}-${format(new Date(leave.end_date), 'd MMM')}`
                                }
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-2xl bg-white border-gray-300">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2" style={{ color: enconBlack }}>
              <Upload className="w-6 h-6" style={{ color: enconOrange }} />
              Import Leave Data
            </DialogTitle>
          </DialogHeader>
          
          {/* Upload Mode Tabs */}
          <div className="flex gap-2 border-b border-gray-200 pb-2">
            <button
              onClick={() => setUploadMode('screenshot')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                uploadMode === 'screenshot' 
                  ? 'bg-orange-100 text-orange-700 border-b-2 border-orange-500' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Image className="w-4 h-4 inline mr-2" />
              Screenshot
            </button>
            <button
              onClick={() => setUploadMode('csv')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                uploadMode === 'csv' 
                  ? 'bg-orange-100 text-orange-700 border-b-2 border-orange-500' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileSpreadsheet className="w-4 h-4 inline mr-2" />
              CSV/Excel
            </button>
          </div>
          
          <div className="space-y-4 py-4">
            {uploadMode === 'screenshot' ? (
              <>
                <p className="text-sm" style={{ color: enconGrey }}>
                  Upload screenshots from your Staffology calendar. The AI will extract data for you to <strong>review and correct</strong> before saving.
                </p>
                
                {/* Drop Zone */}
                <div
                  onDrop={handleDrop}
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onClick={() => fileInputRef.current?.click()}
                  className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                    dragActive 
                      ? 'border-orange-400 bg-orange-50' 
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  
                  <Image className="w-10 h-10 mx-auto mb-3" style={{ color: enconGrey }} />
                  <p className="mb-1" style={{ color: enconBlack }}>
                    <span className="font-semibold" style={{ color: enconOrange }}>Click to upload</span> or drag and drop
                  </p>
                  <p className="text-sm" style={{ color: enconGrey }}>PNG, JPG, PDF • Multiple files allowed</p>
                </div>

                {/* Selected Files Preview */}
                {selectedFiles.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium" style={{ color: enconBlack }}>
                        {selectedFiles.length} file(s) selected
                      </span>
                      <button
                        onClick={() => setSelectedFiles([])}
                        className="text-sm text-red-500 hover:text-red-600"
                      >
                        Clear all
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 max-h-[150px] overflow-y-auto">
                      {selectedFiles.map((file, index) => (
                        <div 
                          key={index} 
                          className="relative group bg-gray-100 rounded-lg p-1.5 border border-gray-200"
                        >
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className="w-full h-16 object-cover rounded"
                          />
                          <button
                            onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-sm" style={{ color: enconGrey }}>
                  Import from CSV or Excel file. Expected columns: <code className="bg-gray-100 px-1 rounded">employee_id</code>, <code className="bg-gray-100 px-1 rounded">employee_name</code>, <code className="bg-gray-100 px-1 rounded">start_date</code>, <code className="bg-gray-100 px-1 rounded">end_date</code>, <code className="bg-gray-100 px-1 rounded">leave_type</code>
                </p>
                
                <div
                  onClick={() => csvInputRef.current?.click()}
                  className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                >
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleCsvUpload}
                    className="hidden"
                  />
                  
                  <FileSpreadsheet className="w-10 h-10 mx-auto mb-3" style={{ color: enconGrey }} />
                  <p className="mb-1" style={{ color: enconBlack }}>
                    <span className="font-semibold" style={{ color: enconOrange }}>Click to select file</span>
                  </p>
                  <p className="text-sm" style={{ color: enconGrey }}>CSV or Excel (.xlsx) files</p>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => { setShowUploadModal(false); setSelectedFiles([]); }}
              className="px-5 border-gray-300"
              disabled={uploading}
            >
              Cancel
            </Button>
            {uploadMode === 'screenshot' && (
              <Button
                onClick={handleUpload}
                disabled={selectedFiles.length === 0 || uploading}
                className="px-5 text-white"
                style={{ backgroundColor: enconOrange }}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Extract & Preview
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview/Verification Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden bg-white border-gray-300">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2" style={{ color: enconBlack }}>
              <Check className="w-6 h-6 text-green-600" />
              Verify & Correct Leave Records
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-2">
            <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
              <p className="text-sm text-yellow-800">
                Please review and correct any errors before saving. Click on dates to edit them.
              </p>
            </div>
            
            <div className="overflow-y-auto max-h-[50vh] border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Employee</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">ID</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Start Date</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">End Date</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Type</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRecords.map((record, index) => (
                    <tr key={index} className={`border-t ${!record.employee_exists ? 'bg-orange-50' : ''}`}>
                      <td className="px-3 py-2">
                        <Input
                          value={record.employee_name || ''}
                          onChange={(e) => handleEditRecord(index, 'employee_name', e.target.value)}
                          className="h-8 text-sm"
                          placeholder="Name"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          value={record.employee_id || ''}
                          onChange={(e) => handleEditRecord(index, 'employee_id', e.target.value)}
                          className="h-8 text-sm w-28"
                          placeholder="ID"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="date"
                          value={record.start_date || ''}
                          onChange={(e) => handleEditRecord(index, 'start_date', e.target.value)}
                          className="h-8 text-sm"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="date"
                          value={record.end_date || ''}
                          onChange={(e) => handleEditRecord(index, 'end_date', e.target.value)}
                          className="h-8 text-sm"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={record.leave_type || 'holiday'}
                          onChange={(e) => handleEditRecord(index, 'leave_type', e.target.value)}
                          className="h-8 px-2 text-sm border rounded w-full"
                        >
                          <option value="holiday">Holiday</option>
                          <option value="half_day_am">Half Day (AM)</option>
                          <option value="half_day_pm">Half Day (PM)</option>
                          <option value="special">Special</option>
                          <option value="sick">Sick</option>
                        </select>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => handleDeleteRecord(index)}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="flex justify-between items-center mt-4">
              <Button
                variant="outline"
                onClick={handleAddRecord}
                className="text-sm"
              >
                + Add Record
              </Button>
              <span className="text-sm text-gray-500">{previewRecords.length} record(s)</span>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => { setShowPreviewModal(false); setPreviewRecords([]); }}
              className="px-5 border-gray-300"
              disabled={savingVerified}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveVerified}
              disabled={previewRecords.length === 0 || savingVerified}
              className="px-5 text-white"
              style={{ backgroundColor: enconOrange }}
            >
              {savingVerified ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Save {previewRecords.length} Record(s)
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupAbsenceCalendar;
